import React from 'react';
import PropTypes from 'prop-types';
import { Mutation, Query, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';

const styles = theme => ({
  content: {
    minWidth: 500,
    minHeight: 300,
  },
  recipientList: {
    maxHeight: 200,
    overflow: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  formControl: {
    minWidth: '100%',
    marginTop: theme.spacing(2),
  },
  successBox: {
    padding: theme.spacing(2),
    backgroundColor: '#4caf50',
    color: 'white',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
  errorBox: {
    padding: theme.spacing(2),
    backgroundColor: '#f44336',
    color: 'white',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
  sequenceInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
});

const GET_ACTIVE_SEQUENCES = gql`
  query GetActiveSequences {
    emailSequences {
      id
      name
      description
      active
      steps {
        id
        position
      }
    }
  }
`;

const ADD_USERS_TO_SEQUENCE = gql`
  mutation AddUsersToSequence($sequenceId: ID!, $userIds: [ID!]!) {
    add_users_to_sequence(sequenceId: $sequenceId, userIds: $userIds) {
      enrolledCount
      errors
    }
  }
`;

const BULK_ADD_FILTERED_USERS = gql`
  mutation BulkAddFilteredUsersToSequence(
    $sequenceId: ID!
    $filterList: InventoryUserFilterInput
    $nameSearch: String
    $distanceSearch: String
    $distanceWithin: Int
    $tgFilter: String
    $tagFilter: String
    $orderBy: String
    $order: String
    $onlyUsers: Boolean
  ) {
    bulk_add_filtered_users_to_sequence(
      sequenceId: $sequenceId
      filterList: $filterList
      nameSearch: $nameSearch
      distanceSearch: $distanceSearch
      distanceWithin: $distanceWithin
      tgFilter: $tgFilter
      tagFilter: $tagFilter
      orderBy: $orderBy
      order: $order
      onlyUsers: $onlyUsers
    ) {
      jobId
      totalCount
      errors
    }
  }
`;

const TAB_SELECTED = 0;
const TAB_ALL_FILTERED = 1;

class AddToSequenceDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      selectedSequenceId: '',
      success: false,
      errorMessage: '',
      activeTab: TAB_SELECTED,
      bulkSubmitted: false,
      bulkTotalCount: 0,
      bulkLoading: false,
    };
  }

  handleOpen = () => {
    this.setState({
      open: true,
      selectedSequenceId: '',
      success: false,
      errorMessage: '',
      activeTab: TAB_SELECTED,
      bulkSubmitted: false,
      bulkTotalCount: 0,
      bulkLoading: false,
    });
  };

  handleClose = () => {
    this.setState({
      open: false,
      selectedSequenceId: '',
      success: false,
      errorMessage: '',
      activeTab: TAB_SELECTED,
      bulkSubmitted: false,
      bulkTotalCount: 0,
      bulkLoading: false,
    });
    if (this.props.loadBulkItemsList) {
      this.props.loadBulkItemsList([]);
    }
  };

  getSelectedUsers = () => {
    const { data, selected } = this.props;
    if (!data || !Array.isArray(data) || !selected || !Array.isArray(selected)) {
      return [];
    }
    const selectedUsers = data.filter(user => selected.includes(user.id));
    return selectedUsers;
  };

  handleAddToSequence = (addUsersToSequence) => {
    const { selectedSequenceId } = this.state;
    const selectedUsers = this.getSelectedUsers();
    const userIds = (selectedUsers || []).map((u) => u?.user?.id).filter(Boolean);
    const skippedCount = (selectedUsers || []).length - userIds.length;

    if (!selectedSequenceId) {
      this.setState({ errorMessage: 'Please select a sequence' });
      return;
    }

    if (!selectedUsers || selectedUsers.length === 0) {
      this.setState({ errorMessage: 'No users selected' });
      return;
    }

    if (userIds.length === 0) {
      this.setState({
        errorMessage: 'No selected inventory users have registered accounts yet (no associated User).'
      });
      return;
    }

    addUsersToSequence({
      variables: {
        sequenceId: selectedSequenceId,
        userIds,
      },
    })
      .then(({ data }) => {
        if (data.add_users_to_sequence.errors && data.add_users_to_sequence.errors.length > 0) {
          this.setState({ errorMessage: data.add_users_to_sequence.errors.join(', ') });
        } else {
          const note = skippedCount > 0 ? ` Skipped ${skippedCount} lead(s) with no User account.` : '';
          this.setState({ success: true, errorMessage: note ? `Success.${note}` : '' });
          setTimeout(() => {
            this.handleClose();
          }, 2000);
        }
      })
      .catch(error => {
        this.setState({ errorMessage: `Error: ${error.message}` });
      });
  };

  handleBulkAddAll = () => {
    const { selectedSequenceId } = this.state;
    const { client, filterParams, inventoryUsersState } = this.props;

    if (!selectedSequenceId) {
      this.setState({ errorMessage: 'Please select a sequence' });
      return;
    }

    this.setState({ bulkLoading: true, errorMessage: '' });

    const variables = {
      sequenceId: selectedSequenceId,
      filterList: filterParams || {},
      nameSearch: inventoryUsersState?.nameSearch || null,
      distanceSearch: inventoryUsersState?.distanceSearch || null,
      distanceWithin: inventoryUsersState?.distanceWithin || null,
      orderBy: inventoryUsersState?.orderBy || 'id',
      order: inventoryUsersState?.order || 'desc',
      onlyUsers: this.props.onlyUsers || false,
    };

    client.mutate({
      mutation: BULK_ADD_FILTERED_USERS,
      variables,
    })
      .then(({ data }) => {
        const result = data.bulk_add_filtered_users_to_sequence;
        if (result.errors && result.errors.length > 0) {
          this.setState({ errorMessage: result.errors.join(', '), bulkLoading: false });
        } else {
          this.setState({
            bulkSubmitted: true,
            bulkTotalCount: result.totalCount,
            bulkLoading: false,
          });
        }
      })
      .catch(error => {
        this.setState({ errorMessage: `Error: ${error.message}`, bulkLoading: false });
      });
  };

  render() {
    const { classes, totalFilteredCount } = this.props;
    const { open, selectedSequenceId, success, errorMessage, activeTab, bulkSubmitted, bulkTotalCount, bulkLoading } = this.state;
    const selectedUsers = this.getSelectedUsers();

    return (
      <>
        <Tooltip title="Add to Sequence">
          <IconButton
            onClick={this.handleOpen}
            color="inherit"
            aria-label="Add to Sequence"
          >
            <PlaylistAddIcon />
          </IconButton>
        </Tooltip>

        <Dialog open={open} onClose={this.handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>Add Users to Sequence</DialogTitle>
          <DialogContent className={classes.content}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => this.setState({ activeTab: val, errorMessage: '', success: false, bulkSubmitted: false })}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label={`Selected (${selectedUsers.length})`} />
              <Tab label={`All Filtered (${totalFilteredCount || 0})`} />
            </Tabs>

            <Query query={GET_ACTIVE_SEQUENCES}>
              {({ loading, error, data }) => {
                if (loading) {
                  return (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                  );
                }

                if (error) {
                  return (
                    <Typography color="error">
                      Error loading sequences: {error.message}
                    </Typography>
                  );
                }

                const activeSequences = (data.emailSequences || []).filter(seq => seq.active);
                const selectedSequence = activeSequences.find(seq => seq.id === selectedSequenceId);

                return (
                  <>
                    {activeTab === TAB_SELECTED && (
                      <>
                        <Typography variant="body1" gutterBottom style={{ marginTop: 16 }}>
                          Selected Users ({selectedUsers.length}):
                        </Typography>
                        <Box className={classes.recipientList}>
                          <List dense>
                            {selectedUsers.map(user => (
                              <ListItem key={user.id}>
                                <ListItemText
                                  primary={user.name}
                                  secondary={user.email}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </>
                    )}

                    {activeTab === TAB_ALL_FILTERED && !bulkSubmitted && (
                      <Box className={classes.infoBox}>
                        <Typography variant="body1">
                          <strong>{totalFilteredCount || 0}</strong> users match the current filters.
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                          All matching users will be enrolled in the selected sequence.
                          Users already enrolled, unsubscribed, or without accounts will be skipped automatically.
                          This runs in the background.
                        </Typography>
                      </Box>
                    )}

                    {!bulkSubmitted && (
                      <FormControl variant="outlined" className={classes.formControl}>
                        <InputLabel>Select Sequence</InputLabel>
                        <Select
                          value={selectedSequenceId}
                          onChange={(e) => {
                            this.setState({ selectedSequenceId: e.target.value, errorMessage: '' });
                          }}
                          label="Select Sequence"
                        >
                          <MenuItem value="">
                            <em>Select a sequence</em>
                          </MenuItem>
                          {activeSequences.length === 0 ? (
                            <MenuItem disabled>
                              <em>No active sequences available</em>
                            </MenuItem>
                          ) : (
                            activeSequences.map(sequence => (
                              <MenuItem key={sequence.id} value={sequence.id}>
                                {sequence.name}
                                <Chip
                                  label={`${sequence.steps?.length || 0} steps`}
                                  size="small"
                                  style={{ marginLeft: 8 }}
                                />
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    )}

                    {selectedSequence && !bulkSubmitted && (
                      <Box className={classes.sequenceInfo}>
                        <Typography variant="subtitle2" gutterBottom>
                          {selectedSequence.name}
                        </Typography>
                        {selectedSequence.description && (
                          <Typography variant="body2" color="textSecondary">
                            {selectedSequence.description}
                          </Typography>
                        )}
                        <Typography variant="body2" style={{ marginTop: 8 }}>
                          <strong>
                            {activeTab === TAB_SELECTED ? selectedUsers.length : (totalFilteredCount || 0)}
                          </strong> user(s) will be enrolled in this sequence
                        </Typography>
                      </Box>
                    )}

                    {bulkSubmitted && (
                      <Box className={classes.successBox}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                          Enrollment started
                        </Typography>
                        <Typography variant="body2" style={{ marginTop: 8 }}>
                          {bulkTotalCount} user(s) are being enrolled in the background.
                          Duplicates and unsubscribed users will be skipped automatically.
                          You can close this dialog — no further action is needed.
                        </Typography>
                      </Box>
                    )}

                    {success && activeTab === TAB_SELECTED && (
                      <Box className={classes.successBox}>
                        <Typography>
                          Successfully added {selectedUsers.length} user(s) to the sequence!
                        </Typography>
                      </Box>
                    )}

                    {errorMessage && !bulkSubmitted && (
                      <Box className={classes.errorBox}>
                        <Typography>{errorMessage}</Typography>
                      </Box>
                    )}
                  </>
                );
              }}
            </Query>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleClose} color="default">
              {bulkSubmitted || success ? 'Close' : 'Cancel'}
            </Button>
            {activeTab === TAB_SELECTED && !bulkSubmitted && (
              <Mutation mutation={ADD_USERS_TO_SEQUENCE}>
                {(addUsersToSequence, { loading }) => (
                  <Button
                    onClick={() => this.handleAddToSequence(addUsersToSequence)}
                    color="primary"
                    variant="contained"
                    disabled={loading || success || !selectedSequenceId}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Add Selected'}
                  </Button>
                )}
              </Mutation>
            )}
            {activeTab === TAB_ALL_FILTERED && !bulkSubmitted && (
              <Button
                onClick={this.handleBulkAddAll}
                color="primary"
                variant="contained"
                disabled={bulkLoading || !selectedSequenceId || !totalFilteredCount}
              >
                {bulkLoading ? <CircularProgress size={24} /> : `Add All ${totalFilteredCount || 0} Users`}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

AddToSequenceDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  data: PropTypes.array,
  selected: PropTypes.array,
  loadBulkItemsList: PropTypes.func,
  filterParams: PropTypes.object,
  inventoryUsersState: PropTypes.object,
  totalFilteredCount: PropTypes.number,
  onlyUsers: PropTypes.bool,
};

AddToSequenceDialog.defaultProps = {
  data: [],
  selected: [],
  loadBulkItemsList: null,
  filterParams: {},
  inventoryUsersState: {},
  totalFilteredCount: 0,
  onlyUsers: false,
};

export default withStyles(styles)(withApollo(AddToSequenceDialog));
